/**
 * Extração determinística de "Em resumo" e de perguntas/respostas a partir do
 * próprio markdown do artigo. Nada é gerado, reescrito ou inventado: apenas
 * trechos literais do texto são reaproveitados.
 */

function limparInline(texto: string): string {
  return texto
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // imagens
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links -> texto
    .replace(/`([^`]*)`/g, "$1")
    .replace(/[*_]{1,3}([^*_]+)[*_]{1,3}/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

interface Bloco {
  tipo: "heading" | "paragrafo" | "item";
  nivel: number;
  texto: string;
}

function blocos(md: string): Bloco[] {
  const out: Bloco[] = [];
  const linhas = md.split(/\r?\n/);
  let paragrafo: string[] = [];
  let dentroDeCodigo = false;

  const fecharParagrafo = () => {
    if (paragrafo.length) {
      const texto = limparInline(paragrafo.join(" "));
      if (texto) out.push({ tipo: "paragrafo", nivel: 0, texto });
      paragrafo = [];
    }
  };

  for (const linhaBruta of linhas) {
    const linha = linhaBruta.trim();
    if (/^```/.test(linha)) {
      dentroDeCodigo = !dentroDeCodigo;
      fecharParagrafo();
      continue;
    }
    if (dentroDeCodigo) continue;
    if (!linha) {
      fecharParagrafo();
      continue;
    }
    const heading = linha.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      fecharParagrafo();
      const texto = limparInline(heading[2]);
      if (texto) out.push({ tipo: "heading", nivel: heading[1].length, texto });
      continue;
    }
    const item = linha.match(/^(?:[-*+]|\d+\.)\s+(.*)$/);
    if (item) {
      fecharParagrafo();
      const texto = limparInline(item[1]);
      if (texto) out.push({ tipo: "item", nivel: 0, texto });
      continue;
    }
    paragrafo.push(linha);
  }
  fecharParagrafo();
  return out;
}

const SECOES_IGNORADAS =
  /^(refer[êe]ncias?|bibliografia|sobre o autor|leia tamb[ée]m|conclus[ãa]o final)\b/i;

function primeiraFrase(texto: string, max = 190): string {
  const frase = texto.split(/(?<=[.!?])\s+/)[0] ?? texto;
  const escolhida = frase.length < 40 ? texto : frase;
  return escolhida.length > max ? `${escolhida.slice(0, max - 1).trimEnd()}…` : escolhida;
}

/** 3 a 5 tópicos vindos do texto: títulos das seções ou 1ª frase dos parágrafos. */
export function extrairResumo(md: string): string[] {
  const bs = blocos(md);

  const headings = bs
    .filter((b) => b.tipo === "heading" && b.nivel >= 2 && b.nivel <= 3)
    .map((b) => b.texto)
    .filter((t) => !SECOES_IGNORADAS.test(t) && !t.endsWith("?"));

  const topicos: string[] = [];
  for (const h of headings) {
    if (topicos.length >= 5) break;
    if (!topicos.includes(h)) topicos.push(primeiraFrase(h));
  }

  if (topicos.length < 3) {
    for (const b of bs) {
      if (topicos.length >= 5) break;
      if (b.tipo !== "paragrafo" && b.tipo !== "item") continue;
      if (b.texto.length < 60) continue;
      const frase = primeiraFrase(b.texto);
      if (!topicos.includes(frase)) topicos.push(frase);
    }
  }

  return topicos.length >= 3 ? topicos.slice(0, 5) : [];
}

export interface Faq {
  pergunta: string;
  resposta: string;
}

/** Perguntas/respostas já existentes no artigo (títulos terminados em "?"). */
export function extrairFaq(md: string): Faq[] {
  const bs = blocos(md);
  const faqs: Faq[] = [];

  bs.forEach((b, i) => {
    if (b.tipo !== "heading" || !b.texto.endsWith("?")) return;
    const respostas: string[] = [];
    for (let j = i + 1; j < bs.length; j += 1) {
      const prox = bs[j];
      if (prox.tipo === "heading") break;
      respostas.push(prox.texto);
      if (respostas.join(" ").length > 320) break;
    }
    const resposta = respostas.join(" ").trim();
    if (resposta.length >= 40) {
      faqs.push({ pergunta: b.texto, resposta: resposta.slice(0, 900) });
    }
  });

  return faqs.length >= 2 ? faqs : [];
}
